import { useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { Banner } from '@/components/feedback/Banner'
import { PageHeader } from '@/components/layout/PageHeader'
import { Button } from '@/components/ui/Button'
import { MoreMenu, type MoreMenuItem } from '@/components/ui/MoreMenu'
import { StatusPill } from '@/components/ui/StatusPill'
import { Tag } from '@/components/ui/Tag'
import { useSession } from '@/features/auth/api/useSession'
import {
  useDeleteCounterparty,
  useMergeCounterparties,
  useSetCounterpartyActive,
  useUpdateCounterparty,
} from '@/features/counterparties/api/useCounterparties'
import {
  CounterpartyFormSheet,
  type CounterpartyDraft,
} from '@/features/counterparties/components/CounterpartyFormSheet'
import { CounterpartyMergeSheet } from '@/features/counterparties/components/CounterpartyMergeSheet'
import {
  COUNTERPARTY_CATEGORIES,
  counterpartyRights,
} from '@/features/counterparties/counterpartyList'
import {
  counterpartyMergeErrorMessage,
  counterpartySaveErrorMessage,
  directoryErrorMessage,
  referencedMessage,
  referencedRefusal,
} from '@/features/directories/directoryText'
import { describeProblem } from '@/lib/problems'
import type { Counterparty, CounterpartyCategory } from '@/types/domain'

type MenuKey = 'deactivate' | 'activate' | 'merge' | 'delete'

/* The counterparty page's header and the record's acts (S-22b): the eyebrow,
 * the name with a neutral category tag, «Изменить» and «⋯» — and below it the
 * refusals of those acts, above the record they concern.
 *
 * There is no blue button here (Locked visual decision): every act on an
 * existing record is secondary. Each control follows `counterpartyRights`, the
 * server's rule read the server's way — «Изменить» and (de)activation for a
 * holder of read + create, merge and delete for read + manage. Absent rather
 * than greyed: a role that may not act is not shown the act (frontend/README).
 *
 * The edit sheet is seeded from `record`, which the page read with
 * `useCounterparty(id)` — the single read, the only one carrying contacts.
 *
 * `controls` is the page's own slot to the left of the acts — the statement's
 * «Год» (S-22e). It is not an act on the record, so it renders for a reader
 * who may only read, and the actions area exists whenever either is there. */
export function CounterpartyRecordHeader({
  record,
  controls,
}: {
  record: Counterparty
  controls?: ReactNode
}) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { data: session } = useSession()
  const rights = counterpartyRights(session)

  const [editing, setEditing] = useState(false)
  const [merging, setMerging] = useState(false)
  const update = useUpdateCounterparty()
  const setActive = useSetCounterpartyActive()
  const remove = useDeleteCounterparty()
  const merge = useMergeCounterparties()

  const deleteRefusal = referencedRefusal(remove.error)
  /* A merge refused because debit notes name the source closes the sheet and
     is said HERE, where its one way out — deactivation — is a button. Inside
     the sheet the only button is the merge that was just refused. */
  const mergeRefusal = referencedRefusal(merge.error)

  /* The opener owns each sheet's mutation, so the opener clears it: the last
     attempt's refusal belongs to the last attempt (frontend/README). */
  const openEdit = () => {
    update.reset()
    setEditing(true)
  }
  const openMerge = () => {
    merge.reset()
    remove.reset()
    setMerging(true)
  }
  const toggleActive = () => {
    merge.reset()
    remove.reset()
    setActive.mutate({ id: record.id, isActive: !record.isActive })
  }

  const items: MoreMenuItem<MenuKey>[] = [
    ...(rights.edit
      ? [
          record.isActive
            ? { key: 'deactivate' as const, label: t('counterparties.actions.deactivate') }
            : { key: 'activate' as const, label: t('counterparties.actions.activate') },
        ]
      : []),
    ...(rights.manage
      ? [
          { key: 'merge' as const, label: t('counterparties.actions.merge') },
          { key: 'delete' as const, label: t('counterparties.actions.delete') },
        ]
      : []),
  ]

  const select = (key: MenuKey) => {
    if (key === 'merge') {
      openMerge()
      return
    }
    if (key === 'delete') {
      merge.reset()
      remove.mutate(record.id, {
        onSuccess: () => navigate('/counterparties', { replace: true }),
      })
      return
    }
    toggleActive()
  }

  const submit = (values: CounterpartyDraft & { category: CounterpartyCategory }) => {
    update.mutate(
      {
        id: record.id,
        payload: {
          name: values.name.trim(),
          category: values.category,
          taxId: values.taxId.trim(),
          registrationId: values.registrationId.trim(),
          country: values.country.trim(),
          address: values.address.trim(),
          contacts: values.contacts,
        },
      },
      { onSuccess: () => setEditing(false) },
    )
  }

  /* The way out of a refusal that suggests deactivation, offered only to a
     reader who may deactivate and only while there is something to do. */
  const deactivateAction =
    rights.edit && record.isActive ? (
      <Button size="sm" variant="secondary" disabled={setActive.isPending} onClick={toggleActive}>
        {t('counterparties.actions.deactivate')}
      </Button>
    ) : undefined

  return (
    <>
      <PageHeader
        eyebrow={t('counterparties.record.eyebrow')}
        title={
          <span className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
            <span className="min-w-0 break-words">{record.name}</span>
            {/* Neutral, as the list's plain-text category: a category is a
                kind, not a state, and no colour may say which one it is. */}
            <Tag className="text-[12.5px] tracking-normal">
              {t(`counterpartyCategory.${record.category}`)}
            </Tag>
            {record.isActive ? null : (
              <StatusPill tone="neutral">{t('common.form.inactive')}</StatusPill>
            )}
          </span>
        }
        actions={
          controls || rights.edit || items.length > 0 ? (
            <>
              {controls}
              {rights.edit ? (
                <Button size="sm" variant="secondary" onClick={openEdit}>
                  {t('counterparties.actions.edit')}
                </Button>
              ) : null}
              {items.length > 0 ? (
                <MoreMenu
                  size="sm"
                  label={t('counterparties.actions.more')}
                  items={items}
                  onSelect={select}
                />
              ) : null}
            </>
          ) : undefined
        }
      />

      {deleteRefusal ? (
        <Banner
          tone="attention"
          title={t('counterparties.refusals.deleteTitle')}
          action={
            deleteRefusal.suggestion === 'merge' ? (
              rights.manage ? (
                <Button size="sm" variant="secondary" onClick={openMerge}>
                  {t('counterparties.actions.merge')}
                </Button>
              ) : undefined
            ) : (
              deactivateAction
            )
          }
        >
          {referencedMessage(deleteRefusal, t)}
        </Banner>
      ) : null}

      {mergeRefusal ? (
        <Banner tone="attention" title={t('counterparties.refusals.mergeTitle')} action={deactivateAction}>
          {counterpartyMergeErrorMessage(merge.error, t)}
        </Banner>
      ) : null}

      {/* The refusals with no sheet of their own: (de)activation, and a delete
          refused for a reason that names no records. A Banner whose one act is
          hiding it — «Повторить» would send the same refused request. */}
      {[setActive, remove].map((mutation, index) =>
        mutation.error && !(mutation === remove && deleteRefusal) ? (
          <div key={index} role="alert">
            <Banner
              tone="attention"
              title={t(describeProblem(mutation.error).titleKey)}
              action={
                <Button size="sm" variant="ghost" onClick={() => mutation.reset()}>
                  {t('common.actions.dismiss')}
                </Button>
              }
            >
              {directoryErrorMessage(mutation.error, t)}
            </Banner>
          </div>
        ) : null,
      )}

      {rights.edit ? (
        <CounterpartyFormSheet
          open={editing}
          onClose={() => setEditing(false)}
          record={record}
          categories={COUNTERPARTY_CATEGORIES}
          pending={update.isPending}
          error={update.error ? counterpartySaveErrorMessage(update.error, t) : undefined}
          onSubmit={submit}
        />
      ) : null}

      {rights.manage ? (
        <CounterpartyMergeSheet
          open={merging}
          onClose={() => setMerging(false)}
          source={record}
          pending={merge.isPending}
          error={
            merge.error && !mergeRefusal
              ? counterpartyMergeErrorMessage(merge.error, t)
              : undefined
          }
          onConfirm={(targetId) =>
            merge.mutate(
              { targetId, sourceId: record.id },
              {
                // The source is gone after a merge; the kept record is where
                // the reader continues.
                onSuccess: () => {
                  setMerging(false)
                  navigate(`/counterparties/${targetId}`, { replace: true })
                },
                onError: (error) => {
                  if (referencedRefusal(error)) setMerging(false)
                },
              },
            )
          }
        />
      ) : null}
    </>
  )
}
